/* Custom element: <button-dropdown>
   A toggle button plus a menu that opens as a popover on desktop and a
   bottom sheet (backdrop, scroll lock, focus trap) on mobile. */

(() => {
  if (customElements.get('button-dropdown')) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  // Keep the menu this far clear of the viewport edge before flipping it.
  const EDGE_GUTTER = 8;

  class ButtonDropdown extends HTMLElement {
    // Listeners bound for as long as the element is in the document.
    #listeners = null;
    // Listeners bound only while the menu is open.
    #openListeners = null;
    #toggle = null;
    #menu = null;
    #backdrop = null;
    #mediaQuery = null;

    connectedCallback() {
      this.#toggle = this.querySelector('[data-dropdown-toggle]');
      this.#menu = this.querySelector('[data-dropdown-menu]');
      this.#backdrop = this.querySelector('[data-dropdown-backdrop]');
      if (!this.#toggle || !this.#menu) return;

      this.#mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.#toggle.setAttribute('aria-expanded', 'false');

      this.#listeners = new AbortController();
      const { signal } = this.#listeners;

      this.#toggle.addEventListener('click', this.#onToggleClick, { signal });
      this.#menu.addEventListener('click', this.#onMenuClick, { signal });
      for (const button of this.querySelectorAll('[data-dropdown-close]')) {
        button.addEventListener('click', this.#onCloseClick, { signal });
      }
      this.#backdrop?.addEventListener('click', this.#onBackdropClick, { signal });
    }

    disconnectedCallback() {
      if (this.isOpen) this.close({ restoreFocus: false });
      this.#listeners?.abort();
      this.#listeners = null;
    }

    get isOpen() {
      return this.hasAttribute('data-open');
    }

    get isMobile() {
      return this.#mediaQuery?.matches;
    }

    /** Open the menu. Public so a parent section can drive the dropdown. */
    open() {
      if (this.isOpen) return;
      this.setAttribute('data-open', '');
      this.#toggle.setAttribute('aria-expanded', 'true');

      this.#openListeners = new AbortController();
      const { signal } = this.#openListeners;

      document.addEventListener('keydown', this.#onKeyDown, { signal });
      document.addEventListener('pointerdown', this.#onDocumentPointerDown, { signal });
      window.addEventListener('resize', this.#onResize, { signal });

      if (this.isMobile) {
        document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
        window.theme?.a11y?.trapFocus?.(this.#menu, { elementToFocus: this.#menu });
        return;
      }

      this.#positionDesktop();
    }

    /**
     * Close the menu.
     *
     * @param {{restoreFocus?: boolean}} [options] - pass `restoreFocus: false`
     *   when the menu is closing because focus is going somewhere else
     *   (a menu link, or the element being removed).
     */
    close({ restoreFocus = true } = {}) {
      if (!this.isOpen) return;
      this.removeAttribute('data-open');
      this.removeAttribute('data-align');
      this.#toggle.setAttribute('aria-expanded', 'false');

      this.#openListeners?.abort();
      this.#openListeners = null;

      window.theme?.a11y?.removeTrapFocus?.();
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));

      if (restoreFocus) this.#toggle.focus();
    }

    #onToggleClick = (event) => {
      event.preventDefault();
      if (this.isOpen) {
        this.close();
        return;
      }
      this.open();
    };

    #onCloseClick = (event) => {
      event.preventDefault();
      this.close();
    };

    #onBackdropClick = () => {
      this.close();
    };

    #onMenuClick = (event) => {
      const link = event.target.closest('a[role="menuitem"]');
      if (link) this.close({ restoreFocus: false });
    };

    #onDocumentPointerDown = (event) => {
      if (this.contains(event.target)) return;
      this.close();
    };

    #onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      this.close();
    };

    #onResize = () => {
      if (this.isOpen && !this.isMobile) this.#positionDesktop();
    };

    /** Flip the menu to right-aligned when it would overflow the viewport. */
    #positionDesktop() {
      this.removeAttribute('data-align');
      const rect = this.#menu.getBoundingClientRect();
      if (rect.right > window.innerWidth - EDGE_GUTTER) {
        this.setAttribute('data-align', 'end');
      }
    }
  }

  customElements.define('button-dropdown', ButtonDropdown);
})();
